import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import jwtDecode from "jwt-decode";
import moment from "moment";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import Swal from "sweetalert2";
import CustomIcons from "../../../components/icons/kIcons.component";
import {
  adminInternshipRequestDetail,
  superadminInternshipRequestDetail,
  adminInternshipRequestStatusUpdate,
  superadminInternshipRequestStatusUpdate,
  adminInternshipRequestSubmitForAcceptance,
  adminInternshipRequestInitialRejection,
  superadminInternshipRequestApprove,
  superadminInternshipRequestReject,
  admin_internship_confirm_rankings,
  admin_internship_revert_ranking,
  admin_internship_ranked_applications,
  superadmin_internship_ranked_applications,
  admin_internship_accept_and_forward_rankings,
  superadmin_internship_revert_rankings,
  // adminEvaluateInternshipProposal,
  adminInternshipProposalStatusUpdate,
} from "../admin.api";
import "./style.css";

export default function InternshipRequestDetail(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [rankedData, setRankedData] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [rankingStatus, setRankingStatus] = useState();
  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage or default to 'details'
    return localStorage.getItem(`internship_request_${id}_activeTab`) || 'details';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRankedLoading, setIsRankedLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  let adminType = Number(jwtDecode(props.state.payload).user_type);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (id) {
      localStorage.setItem(`internship_request_${id}_activeTab`, activeTab);
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (id) {
      fetchRequestDetail();
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === "ranked") {
      fetchRankedApplications();
    }
    if (id && activeTab === "selected") {
      fetchSelectedApplications();
    }
  }, [id, activeTab]);

  const fetchRankedApplications = () => {
    setIsRankedLoading(true);
    const adminType = localStorage.getItem("adminType");
    const apiCall =
      adminType === 2
        ? superadmin_internship_ranked_applications(id)
        : admin_internship_ranked_applications(id);

    apiCall
      .then((res) => {
        if (res?.success) {
          setRankedData(res.data?.ranked_applicants || []);
          setRankingStatus(res.data?.ranking_status || "");
        } else {
          setSnackMessage("Failed to load ranked applications");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsRankedLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching ranked applications:", error);
        setSnackMessage("Error loading ranked applications");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsRankedLoading(false);
      });
  };

  const fetchSelectedApplications = () => {
    setIsRankedLoading(true);
    const adminType = localStorage.getItem("adminType");
    const apiCall =
      adminType === 2
        ? superadmin_internship_ranked_applications(id)
        : admin_internship_ranked_applications(id);

    apiCall
      .then((res) => {
        if (res?.success) {
          // Filter applicants with statuses: company_selected, allotted, doing, evaluation, evaluated, certified
          const selectedApplicants = res.data?.ranked_applicants?.filter(
            applicant =>
              applicant.proposal?.status === "company_selected" ||
              applicant.proposal?.status === "allotted" ||
              applicant.proposal?.status === "doing" ||
              applicant.proposal?.status === "evaluation" ||
              applicant.proposal?.status === "evaluated" ||
              applicant.proposal?.status === "certified"
          ) || [];
          setSelectedData(selectedApplicants);
        } else {
          setSnackMessage("Failed to load selected applications");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsRankedLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching selected applications:", error);
        setSnackMessage("Error loading selected applications");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsRankedLoading(false);
      });
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(rankedData[index]);
    e.dataTransfer.effectAllowed = "move";
    // Using a more modern approach for drag data
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Necessary to allow dropping
    const hoveredItem = rankedData[index];

    if (draggedItem === hoveredItem) {
      return;
    }

    let items = rankedData.filter((item) => item !== draggedItem);
    items.splice(index, 0, draggedItem);

    setRankedData(items);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const removeFromRanked = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This application will be marked as rejected for this request.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        let items = [...rankedData];
        items.splice(index, 1);
        setRankedData(items);
      }
    });
  };

  const confirmRankings = () => {
    setIsUpdating(true);
    const payload = {
      internship_request_id: parseInt(id),
      proposal_ids: rankedData.map((item) => item.proposal?.id),
    };

    admin_internship_confirm_rankings(payload)
      .then((res) => {
        if (res?.success) {
          setSnackMessage(res?.message || "Rankings confirmed successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          fetchRankedApplications();
        } else {
          setSnackMessage(res?.message || "Failed to confirm rankings");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((error) => {
        setSnackMessage("Error confirming rankings");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  const acceptAndForwardRankings = () => {
    setIsUpdating(true);
    const payload = {
      internship_request_id: parseInt(id),
    };

    admin_internship_accept_and_forward_rankings(payload)
      .then((res) => {
        if (res?.success) {
          setSnackMessage(res?.message || "Rankings confirmed successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          fetchRankedApplications();
        } else {
          setSnackMessage(res?.message || "Failed to confirm rankings");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((error) => {
        setSnackMessage("Error confirming rankings");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  const superadminRevertRankings = () => {
    Swal.fire({
      title: "Revert to Admin?",
      text: "This will send the rankings back to the admin for review.",
      icon: "question",
      input: "text",
      inputPlaceholder: "Enter reason for reverting...",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Revert to Admin",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsUpdating(true);
        const payload = {
          internship_request_id: parseInt(id),
          sadmin_message: result.value || "Reverted by SuperAdmin"
        };

        superadmin_internship_revert_rankings(payload)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Rankings reverted to admin");
              setIsSnackError(false);
              setSnackOpen(true);
              fetchRankedApplications();
            } else {
              setSnackMessage(res?.message || "Failed to revert rankings");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage("Error reverting rankings");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const revertRankings = () => {
    Swal.fire({
      title: "Revert to System Ranking?",
      text: "This will undo all manual ranking overrides.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0A223D",
      confirmButtonText: "Yes, revert!",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsUpdating(true);
        admin_internship_revert_ranking({ internship_request_id: parseInt(id) })
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Rankings reverted to system generated");
              setIsSnackError(false);
              setSnackOpen(true);
              fetchRankedApplications();
            } else {
              setSnackMessage(res?.message || "Failed to revert rankings");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage("Error reverting rankings");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const fetchRequestDetail = () => {
    setIsLoading(true);
    const apiCall =
      adminType === 2
        ? superadminInternshipRequestDetail(id)
        : adminInternshipRequestDetail(id);

    apiCall
      .then((res) => {
        if (res?.success && res?.data) {
          setData(res.data);
        } else {
          setSnackMessage("Failed to load internship request details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching internship request:", error);
        setSnackMessage("Error loading internship request");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  const handleApproveStatus = (status) => {
    let updateData = { status: status };
    setIsUpdating(true);
    const updateCall =
      adminType === 2
        ? superadminInternshipRequestStatusUpdate(id, updateData)
        : adminInternshipRequestStatusUpdate(id, updateData);

    updateCall
      .then((res) => {
        if (res?.success) {
          setSnackMessage(res?.message || "Status updated successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          setTimeout(() => {
            props.history.push("/admin-internship-requests");
          }, 1500);
        } else {
          setSnackMessage(res?.message || "Failed to update status");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((error) => {
        console.error("Error updating status:", error);
        setSnackMessage(error?.message || "Error while updating status");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  const saMessage = (status) => {
    Swal.fire({
      title: "Remarks",
      input: "textarea",
      inputLabel: "Provide remarks",
      showCancelButton: true,
      confirmButtonColor: "#0A223D",
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputAttributes: {
        "aria-label": "Type your remarks here",
      },
      inputValidator: (value) => {
        if (!value.trim()) {
          return "Please provide remarks";
        }
      },
    }).then((message) => {
      if (message.isConfirmed && message.value.trim()) {
        let requestId = id;

        if (!requestId) {
          setSnackMessage("Internship Request ID is missing");
          setIsSnackError(true);
          setSnackOpen(true);
          return;
        }

        let rdata = {
          status: status,
          admin_remarks: message.value,
        };

        setIsUpdating(true);

        const updateCall =
          adminType === 2
            ? superadminInternshipRequestStatusUpdate(requestId, rdata)
            : adminInternshipRequestStatusUpdate(requestId, rdata);

        updateCall
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Status updated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              setTimeout(() => {
                props.history.push("/admin-internship-requests");
              }, 1500);
            } else {
              setSnackMessage(res?.message || "Failed to update status");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage(error?.message || "Error while updating status");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const showMessageDialog = (title, callback) => {
    Swal.fire({
      title: title,
      input: "textarea",
      inputLabel: "Message",
      showCancelButton: true,
      confirmButtonColor: "#0A223D",
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputAttributes: {
        "aria-label": "Type message here",
      },
      inputValidator: (value) => {
        if (!value.trim()) {
          return "Please provide a message";
        }
      },
    }).then((result) => {
      if (result.isConfirmed && result.value.trim()) {
        callback(result.value);
      }
    });
  };

  const handleAdminSubmitForAcceptance = () => {
    showMessageDialog("Submit for Acceptance", (message) => {
      setIsUpdating(true);
      adminInternshipRequestSubmitForAcceptance(id, {
        admin_message: message,
      })
        .then((res) => {
          if (res?.success) {
            setSnackMessage(res?.message || "Submitted for acceptance successfully");
            setIsSnackError(false);
            setSnackOpen(true);
            setTimeout(() => {
              props.history.push("/admin-internship-requests");
            }, 1500);
          } else {
            setSnackMessage(res?.message || "Failed to submit for acceptance");
            setIsSnackError(true);
            setSnackOpen(true);
          }
          setIsUpdating(false);
        })
        .catch((error) => {
          setSnackMessage(error?.message || "Error submitting for acceptance");
          setIsSnackError(true);
          setSnackOpen(true);
          setIsUpdating(false);
        });
    });
  };

  const handleAdminInitialRejection = () => {
    showMessageDialog("Initial Rejection", (message) => {
      setIsUpdating(true);
      adminInternshipRequestInitialRejection(id, {
        admin_message: message,
      })
        .then((res) => {
          if (res?.success) {
            setSnackMessage(res?.message || "Rejection submitted successfully");
            setIsSnackError(false);
            setSnackOpen(true);
            setTimeout(() => {
              props.history.push("/admin-internship-requests");
            }, 1500);
          } else {
            setSnackMessage(res?.message || "Failed to submit rejection");
            setIsSnackError(true);
            setSnackOpen(true);
          }
          setIsUpdating(false);
        })
        .catch((error) => {
          setSnackMessage(error?.message || "Error submitting rejection");
          setIsSnackError(true);
          setSnackOpen(true);
          setIsUpdating(false);
        });
    });
  };

  const handleSuperadminApprove = () => {
    showMessageDialog("Approve Internship Request", (message) => {
      setIsUpdating(true);
      superadminInternshipRequestApprove(id, {
        sadmin_message: message,
      })
        .then((res) => {
          if (res?.success) {
            setSnackMessage(res?.message || "Request approved successfully");
            setIsSnackError(false);
            setSnackOpen(true);
            setTimeout(() => {
              props.history.push("/admin-internship-requests");
            }, 1500);
          } else {
            setSnackMessage(res?.message || "Failed to approve request");
            setIsSnackError(true);
            setSnackOpen(true);
          }
          setIsUpdating(false);
        })
        .catch((error) => {
          setSnackMessage(error?.message || "Error approving request");
          setIsSnackError(true);
          setSnackOpen(true);
          setIsUpdating(false);
        });
    });
  };

  const handleSuperadminReject = () => {
    showMessageDialog("Reject Internship Request", (message) => {
      setIsUpdating(true);
      superadminInternshipRequestReject(id, {
        sadmin_message: message,
      })
        .then((res) => {
          if (res?.success) {
            setSnackMessage(res?.message || "Request rejected successfully");
            setIsSnackError(false);
            setSnackOpen(true);
            setTimeout(() => {
              props.history.push("/admin-internship-requests");
            }, 1500);
          } else {
            setSnackMessage(res?.message || "Failed to reject request");
            setIsSnackError(true);
            setSnackOpen(true);
          }
          setIsUpdating(false);
        })
        .catch((error) => {
          setSnackMessage(error?.message || "Error rejecting request");
          setIsSnackError(true);
          setSnackOpen(true);
          setIsUpdating(false);
        });
    });
  };

  /* const handleEvaluateApplicant = (proposalId) => {
    if (!proposalId) {
      setSnackMessage("Proposal ID is missing");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    Swal.fire({
      title: "Evaluate Applicant",
      html: `
        <div style="text-align: left;">
          <div class="mb-3 text-sm">
            <label for="evaluate-date" class="form-label">Completion Date</label>
            <input type="date" id="evaluate-date" class="form-control" required>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-remarks" class="form-label">Evaluation Remarks</label>
            <textarea id="evaluate-remarks" class="form-control" rows="3" required></textarea>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-file" class="form-label">Evaluation Report File</label>
            <input type="file" id="evaluate-file" class="form-control" accept=".pdf,.doc,.docx" required>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      confirmButtonText: "Evaluate",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const date = document.getElementById('evaluate-date').value;
        const remarks = document.getElementById('evaluate-remarks').value;
        const file = document.getElementById('evaluate-file').files[0];

        if (!date || !remarks || !file) {
          Swal.showValidationMessage('Please fill all fields and select a file');
          return false;
        }

        return { date, remarks, file };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { date, remarks, file } = result.value;
        
        // Create FormData
        const formData = new FormData();
        formData.append('completion_date', date);
        formData.append('evaluation_remarks', remarks);
        formData.append('evaluation_report_file', file);

        setIsUpdating(true);
        
        // Call the API with FormData
        adminEvaluateInternshipProposal(proposalId, formData)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Applicant evaluated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              // Refresh the selected applications list
              fetchSelectedApplications();
            } else {
              setSnackMessage(res?.message || "Failed to evaluate applicant");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage(error?.message || "Error evaluating applicant");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  }; */

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "success";
      case "company_selected":
        return "info";
      case "selected":
        return "primary";
      case "allotted":
        return "warning";
      case "doing":
        return "info";
      case "evaluation":
        return "primary";
      case "evaluated":
        return "success";
      case "certified":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "success";
      case "company_selected":
        return "info";
      case "selected":
        return "primary";
      case "allotted":
        return "warning";
      case "doing":
        return "info";
      case "evaluation":
        return "primary";
      case "evaluated":
        return "success";
      case "certified":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "muted";
    }
  };

  const getStatusButtonLabel = (currentStatus) => {
    switch (currentStatus) {
      case 'company_selected':
        return 'Allot';
      case 'allotted':
        return 'Doing';
      case 'doing':
        return 'Evaluation';
      case 'evaluation':
        return ''; // No action button for evaluation status
      case 'evaluated':
        return 'Certified'; // Show "Certified" button for evaluated status
      case 'certified':
        return 'Certified'; // Show "Certified" as final status
      default:
        return 'Allot';
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case 'company_selected':
        return 'allotted';
      case 'allotted':
        return 'doing';
      case 'doing':
        return 'evaluation';
      case 'evaluation':
        return 'evaluation'; // Stay in evaluation (no further action)
      case 'evaluated':
        return 'certified'; // Move to certified when clicking Certified button
      case 'certified':
        return 'certified'; // Stay in certified (final status)
      default:
        return 'allotted';
    }
  };

  const handleStatusUpdate = (proposalId, currentStatus) => {
    if (!proposalId) {
      setSnackMessage("Proposal ID is missing");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    const nextStatus = getNextStatus(currentStatus);

    // Don't proceed if already at final status
    if (currentStatus === 'evaluation') {
      setSnackMessage("Already in evaluation status");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    setIsUpdating(true);
    const payload = { status: nextStatus };

    adminInternshipProposalStatusUpdate(proposalId, payload)
      .then((res) => {
        if (res?.success) {
          // Show success modal
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Status changed successfully',
            confirmButtonColor: '#0A223D',
            timer: 2000,
            timerProgressBar: true
          }).then(() => {
            // Refresh the selected applications list
            fetchSelectedApplications();
          });
        } else {
          setSnackMessage(res?.message || "Failed to update status");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((error) => {
        setSnackMessage(error?.message || "Error updating status");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  const handleViewEvaluationDetails = (proposal) => {
    if (!proposal) {
      setSnackMessage("Proposal data is missing");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    Swal.fire({
      title: '<h3 style="margin-bottom:10px;">Evaluation Details</h3>',
      html: `
    <div style="
      text-align:left;
      font-family: Arial, sans-serif;
      line-height:1.6;
    ">
      
      <div style="
        background:#f4f6f8;
        padding:15px;
        border-radius:10px;
        margin-bottom:15px;
      ">
        <p style="margin:0; color:#555; font-size:13px;">Completion Date</p>
        <p style="margin:0; font-weight:600; font-size:15px;">
          ${proposal.completion_date || 'N/A'}
        </p>
      </div>

      <div style="
        background:#f4f6f8;
        padding:15px;
        border-radius:10px;
        margin-bottom:15px;
      ">
        <p style="margin:0; color:#555; font-size:13px;">Evaluation Remarks</p>
        <p style="margin:0; font-weight:600; font-size:15px;">
          ${proposal.evaluation_remarks || 'N/A'}
        </p>
      </div>

      <div style="
        background:#f4f6f8;
        padding:15px;
        border-radius:10px;
        text-align:left;
      ">
        <p style="margin-bottom:10px; color:#555; font-size:13px;">
          Evaluation Report
        </p>

        ${proposal.evaluation_report_file
          ? `
              <a href="${proposal.evaluation_report_file}" target="_blank"
                style="
                  display:inline-block;
                  background:#0A223D;
                  color:#fff;
                  padding:8px 16px;
                  border-radius:6px;
                  text-decoration:none;
                  font-size:14px;
                ">
                📄 View Report
              </a>
            `
          : `<p style="color:#999; font-size:14px;">No report uploaded</p>`
        }
      </div>

    </div>
  `,
      showCancelButton: false,
      confirmButtonColor: '#0A223D',
      confirmButtonText: 'Close',
      width: '500px',
      customClass: {
        popup: 'swal2-border-radius'
      }
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: "Pending", badge: "warning" },
      submitted_for_acceptance: { label: "Submitted for Acceptance", badge: "info" },
      approved: { label: "Approved", badge: "success" },
      rejected: { label: "Rejected", badge: "danger" },
      initially_rejected: { label: "Admin Reverted", badge: "danger" },
    };
    return statusMap[status] || { label: status?.charAt(0).toUpperCase() + status?.slice(1), badge: "secondary" };
  };

  const getRankingStatusInfo = (status) => {
    const statusMap = {
      open: { label: "System Generated", badge: "warning" },
      ranking_submitted: { label: "Ranking Submitted", badge: "primary" },
      ranking_confirmed: { label: "Ranking Confirmed", badge: "success" },
      ranking_reverted: { label: "Ranking Reverted", badge: "secondary" },
    };
    return statusMap[status] || { label: status?.charAt(0).toUpperCase() + status?.slice(1), badge: "secondary" };
  };

  const getQualificationNames = (ids) => {
    if (!Array.isArray(ids) || !data?.all_qualifications) return "--";

    const qualificationsArray = Array.isArray(data.all_qualifications)
      ? data.all_qualifications
      : Object.values(data.all_qualifications);

    return qualificationsArray
      .filter((q) => ids.includes(q.id))
      .map((q) => q.name)
      .join(", ");
  };

  if (isLoading) return <Loader open={true} />;

  if (!data)
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <p>Failed to load internship request details.</p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => props.history.goBack()}
          >
            Go Back
          </button>
        </div>
      </div>
    );

  return (
    <div>
      <Loader open={isUpdating || isRankedLoading} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="view-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <button
              style={{
                backgroundColor: "#0a223d",
                color: "#fff",
                border: "solid 1px #0a223d",
              }}
              type="button"
              className="btn btn-warning btn-sm mr-3"
              onClick={(e) => {
                props.history.push("/admin-internship-requests");
              }}
            >
              Back
            </button>
            <h6 className="mb-0">
              <b>
                {activeTab === "details" ? "Internship Request Details" : 
                 activeTab === "ranked" ? "Ranked Applications" : 
                 "Selected Applications"}
              </b>
            </h6>
          </div>

          {data?.status === "approved" && (
            <div className="tab-navigation d-flex gap-2">
              <button
                className={`btn btn-sm ${activeTab === "details" ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setActiveTab("details")}
              >
                Request Details
              </button>
              <button
                className={`btn btn-sm ${activeTab === "ranked" ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setActiveTab("ranked")}
              >
                Ranked Applications
              </button>
              <button
                className={`btn btn-sm ${activeTab === "selected" ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setActiveTab("selected")}
              >
                Selected Applications
              </button>
            </div>
          )}
        </div>

        <hr />

        {activeTab === "details" ? (
          <div className="data">
            <div className="row d-flex align-items-center">
              <div className="col-3">Company Name</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.company?.company_name || "--"}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Discipline</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.internship_discipline}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Description</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.brief_description}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">No. of Positions</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.number_of_students}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Target Category</div>
              <div className="col-1">:</div>
              <div className="col-7 text-capitalize">
                {data?.target_category?.replace(/_/g, " ")}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Start Date</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {moment(data?.preferred_start_date).format("DD-MM-YYYY")}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">End Date</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {moment(data?.preferred_end_date).format("DD-MM-YYYY")}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Grade Criteria</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.grade_criteria || "--"}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Additional Certifications</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.additional_certifications || "--"}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Qualification Type</div>
              <div className="col-1">:</div>
              <div className="col-7">{data?.qualification_type?.name}</div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Streams</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {getQualificationNames(data?.qualification_type_parents) || "--"}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Qualifications</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {getQualificationNames(data?.qualification_type_subs) || "--"}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Status</div>
              <div className="col-1">:</div>
              <div className="col-7">
                <span className={`badge bg-${getStatusInfo(data?.status).badge}`}>
                  {getStatusInfo(data?.status).label}
                </span>
              </div>
            </div>

            {data?.admin_remarks && (
              <div className="row d-flex align-items-center">
                <div className="col-3">Admin Remarks</div>
                <div className="col-1">:</div>
                <div className="col-7">{data?.admin_remarks}</div>
              </div>
            )}

            <div className="row d-flex align-items-center">
              <div className="col-3">Submitted Date</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {moment(data?.created_at).format("DD-MM-YYYY hh:mm A")}
              </div>
            </div>

            <div className="row d-flex align-items-center">
              <div className="col-3">Last Updated</div>
              <div className="col-1">:</div>
              <div className="col-7">
                {moment(data?.updated_at).format("DD-MM-YYYY hh:mm A")}
              </div>
            </div>

            {adminType !== 2 && data?.status === "pending" && (
              <div className="d-flex justify-content-center mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={handleAdminSubmitForAcceptance}
                >
                  Submit for Acceptance
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleAdminInitialRejection}
                >
                  Initial Rejection
                </button>
              </div>
            )}

            {adminType === 2 && data?.status === "submitted_for_acceptance" && (
              <div className="d-flex justify-content-center mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={handleSuperadminApprove}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleSuperadminReject}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ) : activeTab === "ranked" ? (
          <div className="ranked-applications">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <span className={`badge bg-${getRankingStatusInfo(rankingStatus).badge} fs-6 text-white text-center`}>
                  {getRankingStatusInfo(rankingStatus).label}
                </span>
              </div>

              <div className="d-flex gap-2">
                {(rankingStatus === "ranking_submitted" || rankingStatus === "ranking_reverted") && adminType === 1 && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={revertRankings}
                    title="Revert to System Ranking"
                  >
                    <CustomIcons iconName="refresh" /> Reset to Default
                  </button>
                )}
                {(rankingStatus === "open" || rankingStatus === "ranking_reverted") && adminType === 1 && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={confirmRankings}
                    disabled={rankedData.length === 0}
                  >
                    <CustomIcons iconName="tick" />     Confirm Rankings
                  </button>
                )}
                {rankingStatus === "ranking_submitted" && adminType === 2 && (<>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={superadminRevertRankings}
                    title="Revert to System Ranking"
                  >
                    <CustomIcons iconName="refresh" /> Revert to Admin
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={acceptAndForwardRankings}
                    disabled={rankedData.length === 0}
                  >
                    <CustomIcons iconName="tick" />    Accept and Forward
                  </button>
                </>
                )}
              </div>

            </div>
            {rankingStatus === "ranking_reverted" && (
              <div className="mt-3">
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="fas fa-comment-dots me-2"></i>
                    SuperAdmin Message
                  </span>
                  <input
                    type="text"
                    className="form-control border-left-0"
                    disabled
                    value={data?.sadmin_revert_message || "No revert message provided"}
                  />
                </div>
              </div>
            )}
            {rankedData.length === 0 ? (
              <div className="alert alert-info">{adminType === 2 ? "This ranking is not yet submitted for review." : "No applications found for this request."}</div>
            ) : (
              <div className="ranked-list mt-3">
                {rankedData.map((applicant, idx) => (
                  <div
                    key={applicant.proposal?.id || idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`card mb-2 shadow-sm border-left-primary ${draggedItem === applicant ? "opacity-50 border-primary shadow-lg" : ""
                      }`}
                    style={{ cursor: "move", borderLeft: "4px solid #007bff" }}
                  >
                    <div className="card-body py-2 px-3">
                      <div className="row align-items-center">
                        <div className="col-auto text-muted">
                          <CustomIcons iconName="menu" />
                        </div>
                        <div className="col-auto">
                          <span className="h5 mb-0 text-primary">#{idx + 1}</span>
                          {applicant.admin_rank && (
                            <span className="badge badge-info ml-2" title="Manual Rank">
                              Manual
                            </span>
                          )}
                        </div>
                        <div className="col">
                          <div className="font-weight-bold">{applicant.internship?.full_name}</div>
                          <div className="small text-muted">{applicant.internship?.email}</div>
                        </div>
                        <div className="col-md-2 small">
                          <strong>Score:</strong> {applicant.latest_score}
                          {applicant.previous_score > 0 && (
                            <div className="text-secondary">Prev: {applicant.previous_score}</div>
                          )}
                        </div>
                        <div className="col-md-2 small">
                          <strong>Pref:</strong> Choice #{applicant.preference_order}
                        </div>
                        <div className="col-auto d-flex gap-2">
                          <button
                            className="btn btn-sm btn-link text-primary p-1"
                            onClick={() =>
                              props.history.push(
                                `/admin-internship-applications/${applicant.internship?.id}`
                              )
                            }
                            title="View Profile"
                          >
                            <i className="fa fa-user"></i> Profile
                          </button>
                          <button
                            className="btn btn-sm btn-link text-danger p-1"
                            onClick={() => removeFromRanked(idx)}
                            title="Remove and Reject"
                          >
                            <i className="fa fa-trash"></i> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {rankedData.length > 0 && (
              <p className="text-muted small mt-2">
                * Drag and drop rows to manually adjust ranking. Missing rows after confirmation will
                be marked as rejected.
              </p>)}
          </div>
        ) : activeTab === "selected" ? (
          <div className="selected-applications">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <span className="badge bg-success fs-6 text-white">
                  Company Selected Applicants
                </span>
              </div>
            </div>

            {selectedData.length === 0 ? (
              <div className="alert alert-info">
                No selected applications found for this request.
              </div>
            ) : (
              <div className="selected-list mt-3">
                {selectedData.map((applicant, idx) => (
                  <div
                    key={applicant.proposal?.id || idx}
                    className="card mb-2 shadow-sm border-left-success"
                    style={{ borderLeft: "4px solid #28a745" }}
                  >
                    <div className="card-body py-2 px-3">
                      <div className="row align-items-center">
                        <div className="col-auto" style={{ width: '100px' }}>
                          <span className="h5 mb-0 text-success">#{idx + 1}</span>
                        </div>
                        <div className="col" style={{ maxWidth: '300px' }}>
                          <div className="font-weight-bold">{applicant.internship?.full_name}</div>
                          <div className="small text-muted">{applicant.internship?.email}</div>
                        </div>
                        <div className="col-md-2" style={{ maxWidth: '250px' }}>
                          <div className="small">
                            <strong>Score:</strong> {applicant.latest_score}
                          </div>
                        </div>
                        <div className="col-md-1" style={{ maxWidth: '300px' }}>
                          <div className="small">
                            <strong>Pref:</strong> #{applicant.preference_order}
                          </div>
                        </div>
                        <div className="col-md-2" style={{ maxWidth: '300px' }}>
                          <div className="small">
                            <strong>Status:</strong>
                            <span className={`ms-1 text-${getStatusTextColor(applicant.proposal?.status?.toLowerCase() || 'pending')}`} style={{ fontWeight: '600' }}>
                              {applicant.proposal?.status?.charAt(0).toUpperCase() + applicant.proposal?.status?.slice(1) || 'Pending'}
                            </span>
                          </div>
                        </div>
                        {applicant.proposal?.status === 'evaluated' && (
                          <div className="col-md-2" style={{ maxWidth: '300px' }}>
                            <button
                              className="btn btn-sm btn-link text-primary p-0"
                              onClick={() => handleViewEvaluationDetails(applicant.proposal)}
                              title="View Evaluation Details"
                            >
                              <i className="fa fa-eye"></i> View Evaluation Status
                            </button>
                          </div>
                        )}
                        <div className="col-auto" style={{ maxWidth: '300px' }}>
                          <div className="d-flex gap-5">
                            <button
                              className="btn btn-sm btn-link text-primary p-1"
                              onClick={() =>
                                props.history.push(
                                  `/admin-internship-applications/${applicant.internship?.id}`
                                )
                              }
                              title="View Profile"
                            >
                              <i className="fa fa-user"></i> Profile
                            </button>
                            {getStatusButtonLabel(applicant.proposal?.status) && (
                              <button
                                className={`btn btn-sm ${applicant.proposal?.status === 'allotted' ? 'btn-warning' :
                                  applicant.proposal?.status === 'doing' ? 'btn-info' :
                                    applicant.proposal?.status === 'evaluated' ? 'btn-success' :
                                      applicant.proposal?.status === 'certified' ? 'btn-secondary' :
                                        'btn-success'
                                  }`}
                                onClick={() => handleStatusUpdate(applicant.proposal?.id, applicant.proposal?.status)}
                                title={`Change Status (Current: ${applicant.proposal?.status || 'pending'})`}
                                disabled={applicant.proposal?.status === 'certified'}
                              >
                                <i className="fa fa-exchange-alt"></i> {getStatusButtonLabel(applicant.proposal?.status)}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

      </div>
    </div>
  );
}
